import React, { useCallback, useRef } from 'react';
import { styled } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import CheckboxIcon from '@atlaskit/icon/core/migration/checkbox-checked--checkbox';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { Flex, Box, xcss } from '@atlaskit/primitives';

import { fg } from '@confluence/feature-gating';

import { useIsDisabledContext } from '../IsDisabledContext';
import type { UnsplashResult, SelectedImage } from '../index';

import { isUnsplashImage } from './unsplashAPI';
import { UnsplashImageThumbnail } from './Thumbnail/UnsplashImageThumbnail';
import { PlaceholderThumbnail } from './Thumbnail/PlaceholderThumbnail';
import { UploadedImageThumbnail } from './Thumbnail/UploadedImageThumbnail';
import { NoImageThumbnail } from './Thumbnail/NoImageThumbnail';

const ImagesWrapperStyles = xcss({
	marginTop: 'space.100',
	justifyContent: 'space-between',
	paddingTop: 'space.0',
});

const baseImageStyles = {
	height: '44px',
	width: '46px',
	borderRadius: '4px',
	border: 'none',
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Image = styled.button<{
	imageSrc: string;
	isSelectedImage: boolean;
	isUploadingImage?: boolean;
}>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...baseImageStyles,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	cursor: `${({ isUploadingImage }) => (isUploadingImage ? 'auto' : 'pointer')}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	backgroundImage: `${({ imageSrc }) => `url(${imageSrc})`}`,
	backgroundSize: 'cover',
	backgroundPosition: 'center',
	backgroundRepeat: 'no-repeat',
	position: 'relative',
	display: 'flex',
	justifyContent: 'flex-end',
	alignItems: 'flex-end',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	boxShadow: `${({ isSelectedImage }) =>
		isSelectedImage ? `inset 0 0 0 2px ${token('color.border.selected')}` : 'none'}`,

	'&:focus-visible': {
		outline: '2px solid',
		outlineColor: token('color.border.focused'),
	},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> span': {
		position: 'absolute',
		bottom: token('space.negative.075', '-6px'),
		right: token('space.negative.075', '-6px'),
	},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> span > svg': {
		width: '30px',
		height: '30px',
	},
});

const selectedImageTickStyles = xcss({
	position: 'absolute',
	bottom: 'space.negative.075',
	right: 'space.negative.075',
});

type UnsplashImage = {
	fullSizeImageUrl: string;
	altDescription?: string;
};

type CustomImage = {
	imageId: string;
};

enum ThumbnailType {
	UNSPLASH_DEFAULT_IMAGE,
	UNSPLASH_IMAGE,
	CUSTOM_IMAGE,
	NO_IMAGE,
}

type Thumbnail = {
	type: ThumbnailType;
	image: UnsplashResult | UnsplashImage | CustomImage | undefined;
};

const isUnsplashDefaultImageThumbnail = (
	thumbnail: Thumbnail,
): thumbnail is Thumbnail & { image: UnsplashResult | undefined } => {
	return thumbnail.type === ThumbnailType.UNSPLASH_DEFAULT_IMAGE;
};

const isUnsplashImageThumbnail = (
	thumbnail: Thumbnail,
): thumbnail is Thumbnail & { image: UnsplashImage } => {
	return thumbnail.type === ThumbnailType.UNSPLASH_IMAGE;
};

const isCustomImageThumbnail = (
	thumbnail: Thumbnail,
): thumbnail is Thumbnail & { image: CustomImage } => {
	return thumbnail.type === ThumbnailType.CUSTOM_IMAGE;
};

const isNoImageThumbnail = (
	thumbnail: Thumbnail,
): thumbnail is Thumbnail & { image: undefined } => {
	return thumbnail.type === ThumbnailType.NO_IMAGE;
};

export const renderImagePlaceholders = () => {
	return Array(5)
		.fill(null)
		.map((_, index) => <PlaceholderThumbnail key={index} />);
};

type DefaultImagePickerProps = {
	contentId: string;
	imageAltText?: string;
	selectedImageUrl?: string;
	initialUnsplashResults: UnsplashResult[] | undefined;
	handleImageSelection: (props: SelectedImage) => void;
	analyticsSource: string;
	isUploadingImage: boolean;
	enablePaddingBottom?: boolean;
	isExternalShare: boolean;
	isNoImageAllowed: boolean;
};

export const DefaultImagePicker = ({
	contentId,
	imageAltText,
	selectedImageUrl,
	handleImageSelection,
	initialUnsplashResults,
	analyticsSource,
	isUploadingImage,
	isNoImageAllowed,
	isExternalShare,
}: DefaultImagePickerProps) => {
	const isDisabled = useIsDisabledContext();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onImageClick = useCallback(
		({ fullSizeImageUrl, altDescription, downloadLink }: SelectedImage) => {
			if (!isUploadingImage) {
				handleImageSelection({
					fullSizeImageUrl,
					altDescription,
					downloadLink,
				});

				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						action: 'selected',
						actionSubject: 'image',
						source: analyticsSource,
						attributes: {
							imageSource: 'unsplashDefault',
						},
					},
				}).fire();
			}
		},
		[analyticsSource, createAnalyticsEvent, handleImageSelection, isUploadingImage],
	);

	const firstImage = useRef<Thumbnail>();

	const initialUnsplashResultsIncludes = useCallback(
		(imageUrl: string) => {
			if (!initialUnsplashResults?.length) {
				return false;
			}
			const adjustedUnsplashResults =
				isNoImageAllowed && fg('company_hub_manual_cards_refinement')
					? initialUnsplashResults.toSpliced(0, 2)
					: initialUnsplashResults.toSpliced(0, 1);

			return adjustedUnsplashResults.map((result) => result.fullSizeImageUrl).includes(imageUrl);
		},
		[initialUnsplashResults, isNoImageAllowed],
	);

	const renderGenericUnsplashImage = useCallback(
		({ smallSizeImageUrl, fullSizeImageUrl, downloadLink, description, isSelected, index }) => {
			return (
				<UnsplashImageThumbnail
					key={`thumbnail-${index}`}
					image={smallSizeImageUrl}
					description={description}
					isSelected={isSelected}
					canBeSelected={isUploadingImage}
					onClick={() =>
						onImageClick({
							fullSizeImageUrl,
							altDescription: description,
							downloadLink,
						})
					}
				>
					{isSelected && (
						<Box xcss={selectedImageTickStyles}>
							<CheckboxIcon label="checkmark" color={token('color.icon.brand')} />
						</Box>
					)}
				</UnsplashImageThumbnail>
			);
		},
		[isUploadingImage, onImageClick],
	);

	const renderUnsplashDefaultImage = useCallback(
		(image: UnsplashResult, index: number) => {
			const { fullSizeImageUrl, altDescription, downloadLink, smallSizeImageUrl } = image;
			const isSelected = fullSizeImageUrl === selectedImageUrl;

			return renderGenericUnsplashImage({
				smallSizeImageUrl: smallSizeImageUrl ?? fullSizeImageUrl,
				fullSizeImageUrl,
				description: altDescription,
				downloadLink,
				isSelected,
				index,
			});
		},
		[selectedImageUrl, renderGenericUnsplashImage],
	);

	const renderUnsplashImage = useCallback(
		(image: UnsplashImage, index: number) => {
			const { fullSizeImageUrl, altDescription } = image;
			const isSelected = fullSizeImageUrl === selectedImageUrl;

			return renderGenericUnsplashImage({
				smallSizeImageUrl: fullSizeImageUrl,
				fullSizeImageUrl,
				description: altDescription ?? '',
				downloadLink: undefined,
				isSelected,
				index,
			});
		},
		[selectedImageUrl, renderGenericUnsplashImage],
	);

	const renderUploadedImage = useCallback(
		(image: CustomImage, index: number) => {
			const { imageId } = image;
			const isSelectedImage = imageId === selectedImageUrl;
			return (
				<UploadedImageThumbnail
					key={`thumbnail-${index}`}
					contentId={contentId}
					imageId={imageId}
					isSelected={isSelectedImage}
					isExternalShare={isExternalShare}
					onClick={() =>
						onImageClick({
							fullSizeImageUrl: imageId,
							altDescription: '',
						})
					}
				>
					{isSelectedImage && (
						<Box xcss={selectedImageTickStyles}>
							<CheckboxIcon label="checkmark" color={token('color.icon.brand')} />
						</Box>
					)}
				</UploadedImageThumbnail>
			);
		},
		[selectedImageUrl, contentId, isExternalShare, onImageClick],
	);

	const renderNoImageThumbnail = useCallback(
		(index: number) => {
			const isSelected = selectedImageUrl === '';
			return (
				<NoImageThumbnail
					key={index}
					isSelected={isSelected}
					onClick={() =>
						onImageClick({
							fullSizeImageUrl: '',
							altDescription: '',
						})
					}
				>
					{isSelected && (
						<Box xcss={selectedImageTickStyles}>
							<CheckboxIcon label="checkmark" color={token('color.icon.brand')} />
						</Box>
					)}
				</NoImageThumbnail>
			);
		},
		[selectedImageUrl, onImageClick],
	);

	if (!fg('company_hub_manual_cards_refinement')) {
		if (
			selectedImageUrl &&
			isUnsplashImage(selectedImageUrl) &&
			initialUnsplashResults?.length &&
			!initialUnsplashResultsIncludes(selectedImageUrl)
		) {
			firstImage.current = {
				type: ThumbnailType.UNSPLASH_IMAGE,
				image: { fullSizeImageUrl: selectedImageUrl, altDescription: imageAltText },
			};
		} else {
			firstImage.current = {
				type: ThumbnailType.UNSPLASH_DEFAULT_IMAGE,
				image: initialUnsplashResults?.[0],
			};
		}
	} else {
		if (selectedImageUrl && !isUnsplashImage(selectedImageUrl)) {
			firstImage.current = {
				type: ThumbnailType.CUSTOM_IMAGE,
				image: { imageId: selectedImageUrl },
			};
		}

		if (
			selectedImageUrl &&
			isUnsplashImage(selectedImageUrl) &&
			initialUnsplashResults?.length &&
			!initialUnsplashResultsIncludes(selectedImageUrl)
		) {
			firstImage.current = {
				type: ThumbnailType.UNSPLASH_IMAGE,
				image: { fullSizeImageUrl: selectedImageUrl, altDescription: imageAltText },
			};
		}

		if (!firstImage.current) {
			firstImage.current = {
				type: ThumbnailType.UNSPLASH_DEFAULT_IMAGE,
				image: initialUnsplashResults?.[0],
			};
		}

		// when initialUnsplashResults are initialized update the image to be the first unsplash image
		if (firstImage.current && !firstImage.current.image && initialUnsplashResults?.length) {
			firstImage.current = {
				type: ThumbnailType.UNSPLASH_DEFAULT_IMAGE,
				image: initialUnsplashResults?.[0],
			};
		}
	}

	const secondImage =
		isNoImageAllowed && fg('company_hub_manual_cards_refinement')
			? {
					type: ThumbnailType.NO_IMAGE,
					image: undefined,
				}
			: {
					type: ThumbnailType.UNSPLASH_DEFAULT_IMAGE,
					image: initialUnsplashResults?.[1],
				};

	const imagesToDisplay = [
		firstImage.current,
		secondImage,
		{
			type: ThumbnailType.UNSPLASH_DEFAULT_IMAGE,
			image: initialUnsplashResults?.[2],
		},
		{
			type: ThumbnailType.UNSPLASH_DEFAULT_IMAGE,
			image: initialUnsplashResults?.[3],
		},
		{
			type: ThumbnailType.UNSPLASH_DEFAULT_IMAGE,
			image: initialUnsplashResults?.[4],
		},
	];

	const renderThumbnails = useCallback(
		(thumbnail: Thumbnail, index) => {
			if (isNoImageThumbnail(thumbnail)) {
				return renderNoImageThumbnail(index);
			}

			if (!thumbnail.image) {
				return <PlaceholderThumbnail key={index} />;
			}

			if (isUnsplashDefaultImageThumbnail(thumbnail)) {
				return renderUnsplashDefaultImage(thumbnail.image, index);
			}

			if (isUnsplashImageThumbnail(thumbnail)) {
				return renderUnsplashImage(thumbnail.image, index);
			}

			if (isCustomImageThumbnail(thumbnail)) {
				return renderUploadedImage(thumbnail.image, index);
			}

			return <PlaceholderThumbnail key={index} />;
		},
		[renderUnsplashDefaultImage, renderUnsplashImage, renderUploadedImage, renderNoImageThumbnail],
	);

	const renderImageButton = (unsplashResult: UnsplashResult | undefined) => {
		if (!unsplashResult) {
			return <PlaceholderThumbnail />;
		}

		const { fullSizeImageUrl, altDescription, smallSizeImageUrl } = unsplashResult;
		const isSelectedImage = fullSizeImageUrl === selectedImageUrl;

		return (
			<Image
				key={unsplashResult.id}
				// default images have a thumbnail property, everything else does not
				imageSrc={smallSizeImageUrl ?? fullSizeImageUrl}
				isSelectedImage={isSelectedImage}
				aria-label={altDescription}
				isUploadingImage={isUploadingImage}
				onClick={() => onImageClick(unsplashResult)}
			>
				{isSelectedImage && <CheckboxIcon label="checkmark" color={token('color.icon.brand')} />}
			</Image>
		);
	};

	return (
		<>
			{fg('company_hub_manual_cards_refinement') ? (
				<Flex xcss={ImagesWrapperStyles} testId="default-image-picker">
					{isDisabled ? renderImagePlaceholders() : imagesToDisplay.map(renderThumbnails)}
				</Flex>
			) : (
				<Flex xcss={ImagesWrapperStyles} testId="default-image-picker">
					{!initialUnsplashResults?.length || isDisabled
						? renderImagePlaceholders()
						: imagesToDisplay.map((i) => i.image as UnsplashResult).map(renderImageButton)}
				</Flex>
			)}
		</>
	);
};
